import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';

import { useLanguage } from '../../../Context/LanguageContext';

import SystemSettingView from './SystemSettingView.js';
import CoachSettingView from './CoachSettingView.js';
import NewMemberShipAndClassView from './NewMemberShipAndClassView.js';
import ClassView from './ClassView.js';
import ProductSettingView from './ProductSettingView.js';

const VALID_TABS = ['Class', 'MemberShip', 'System', 'Coach', 'Product'];

function SettingView() {
  const { t } = useLanguage();
  const history = useHistory();
  const location = useLocation();

  const [nowClicked, setNowClicked] = useState(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    return VALID_TABS.includes(tab) ? tab : 'Class';
  });
  const [isClickAddProduct, setIsClickAddProduct] = useState(false);

  // 탭 전환 + URL querystring 동기화 //
  function onClickTab(tab) {
    setNowClicked(tab);
    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    history.replace({ pathname: location.pathname, search: params.toString() });
  }

  const TopLevelRef = useRef();
  const CoachRef = useRef();

  function AddCoachButtonClick() {
    CoachRef.current.setIsClicked(true);
  }

  function onClickAddProductButton() {
    setIsClickAddProduct(true);
  }

  return (
    <TopLevelWrapper ref={TopLevelRef} >
      <TopBarWrapper>
         <TextWrapper>
           <Title>{t('settingView.title')}</Title>
           <Explanation>{t('settingView.desc')}</Explanation>
         </TextWrapper>
      </TopBarWrapper>
      <TotalButtonWrapper>
        <ButtonWrapper>
          <Button isClicked={nowClicked === 'Class'} onClick={() => onClickTab('Class')}>{t('system.categoryClasses')}</Button>
          <Button isClicked={nowClicked === 'MemberShip'} onClick={() => onClickTab('MemberShip')}>{t('system.categoryMembership')}</Button>
          <Button isClicked={nowClicked === 'System'} onClick={() => onClickTab('System')}>{t('system.categorySystem')}</Button>
          <Button isClicked={nowClicked === 'Coach'} onClick={() => onClickTab('Coach')}>{t('settingView.tabCoach')}</Button>
          <Button isClicked={nowClicked === 'Product'} onClick={() => onClickTab('Product')}>{t('settingView.tabProduct')}</Button>
        </ButtonWrapper>
        <CoachButtonWrapper view={nowClicked === 'Coach'}>
          <CoachControlButton onClick={AddCoachButtonClick}>{t('settingView.addCoach')}</CoachControlButton>
        </CoachButtonWrapper>
        <CoachButtonWrapper view={nowClicked === 'Product'}>
          <CoachControlButton onClick={onClickAddProductButton}>{t('settingView.addProduct')}</CoachControlButton>
        </CoachButtonWrapper>
      </TotalButtonWrapper>
      { nowClicked === 'Class' && <ClassView/>}
      { nowClicked === 'MemberShip' && <NewMemberShipAndClassView/>}
      { nowClicked === 'System' && <SystemSettingView /> }
      { nowClicked === 'Coach' && <CoachSettingView ref={CoachRef}/> }
      { nowClicked === 'Product' && <ProductSettingView isClickAddProduct={isClickAddProduct} setIsClickAddProduct={setIsClickAddProduct}/> }
    </TopLevelWrapper>
  )
}

export default SettingView;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  margin-left: 20px;

  // transition: all 0.3s ease-in-out;
`;

const TopBarWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;
`;

const Explanation = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 12px;

  color: ${props => props.theme.subText};

  margin: 0 0 0 0;
`;

const TotalButtonWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;

  z-index: 20;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 8px;

  margin: 28px 0 0 0;
`;

const MemberShipButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 0 20px;

  height: 31px;

  border: ${props => props.nowClicked === 'MemberShip' ? `1px solid ${props.theme.primary}` : `1px solid ${props.theme.subBackground}`};
  border-radius: 11px;
  background-color: ${props => props.nowClicked === 'MemberShip' ? props.theme.primary : props.theme.subBackground};

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.nowClicked === 'MemberShip' ? `#FFFFFF` : props.theme.subText};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.primary};
    border: 1px solid ${props => props.theme.primary};
    color: #FFFFFF;
  }
`;

const Button = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 0px 20px;

  box-sizing: border-box;
  min-width: 40px;
  height: 31px;

  border: ${props => props.isClicked ? `1px solid ${props.theme.primary}` : `1px solid ${props.theme.buttonStroke}`};
  border-radius: 11px;
  background-color: ${props => props.isClicked ? props.theme.primary : props.theme.subBackground};

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.isClicked ? props.theme.background : props.theme.subText};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.primary};
    border: 1px solid ${props => props.theme.primary};
    color: #FFFFFF;
  }
`;

const ClassButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 0 20px;

  height: 31px;

  border: ${props => props.nowClicked === 'Class' ? `1px solid ${props.theme.primary}` : `1px solid ${props.theme.subBackground}`};
  border-radius: 11px;
  background-color: ${props => props.nowClicked === 'Class' ? props.theme.primary : props.theme.subBackground};

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.nowClicked === 'Class' ? `#FFFFFF` : props.theme.subText};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.primary};
    border: 1px solid ${props => props.theme.primary};
    color: #FFFFFF;
  }
`;

const SystemButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 0 20px;

  height: 31px;

  border: ${props => props.nowClicked === 'System' ? `1px solid ${props.theme.primary}` : `1px solid ${props.theme.subBackground}`};
  border-radius: 11px;
  background-color: ${props => props.nowClicked === 'System' ? props.theme.primary : props.theme.subBackground};

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.nowClicked === 'System' ? `#FFFFFF` : props.theme.subText};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.primary};
    border: 1px solid ${props => props.theme.primary};
    color: #FFFFFF;
  }
`;

const CoachButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 0 20px;

  height: 31px;

  border: ${props => props.nowClicked === 'Coach' ? `1px solid ${props.theme.primary}` : `1px solid ${props.theme.subBackground}`};
  border-radius: 11px;
  background-color: ${props => props.nowClicked === 'Coach' ? props.theme.primary : props.theme.subBackground};

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.nowClicked === 'Coach' ? `#FFFFFF` : props.theme.subText};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.primary};
    border: 1px solid ${props => props.theme.primary};
    color: #FFFFFF;
  }
`;

const ProductButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 0 20px;

  height: 31px;

  border: ${props => props.nowClicked === 'Product' ? `1px solid ${props.theme.primary}` : `1px solid ${props.theme.subBackground}`};
  border-radius: 11px;
  background-color: ${props => props.nowClicked === 'Product' ? props.theme.primary : props.theme.subBackground};

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.nowClicked === 'Product' ? `#FFFFFF` : props.theme.subText};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.primary};
    border: 1px solid ${props => props.theme.primary};
    color: #FFFFFF;
  }
`;

const CoachButtonWrapper = styled.div`
  display: ${props => props.view ? `flex` : `none`};
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin-right: 20px;

  gap: 16px;

  transition: all 0.3s ease-in-out;
`;

const CoachControlButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 0 20px;

  height: 40px;

  border-radius: 10px;
  background-color: ${props => props.theme.subKey};

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.subBackground};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.theme.subKeyHover};
    }
  }
`;
